import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Mail } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  logoutAsync,
  selectIsAuthenticated,
  selectUserProfile,
} from "../../authentication/authenticationSlice";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";
import { OrganizationInvitationStatus } from "../enums/OrganizationInvitationStatus";
import {
  selectAcceptInvitationError,
  selectAcceptInvitationStatus,
  selectInvitationPreview,
  selectInvitationPreviewError,
  selectInvitationPreviewStatus,
} from "../organization-slice";
import {
  acceptInvitation,
  fetchInvitationPreview,
} from "../slice-extensions/organization-slice-extension";
import { PENDING_INVITE_TOKEN_KEY } from "../pendingInviteToken";
import { ROUTES } from "../../../routes";

interface InvitationAcceptCardProps {
  token: string;
}

/**
 * The invitation-accept flow: previews the invitation (anonymously), then either
 * routes a signed-out visitor to sign-in/sign-up (stashing the token) or lets a
 * signed-in instructor join the organization.
 */
const InvitationAcceptCard: React.FC<InvitationAcceptCardProps> = ({ token }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const profile = useAppSelector(selectUserProfile);
  const preview = useAppSelector(selectInvitationPreview);
  const previewStatus = useAppSelector(selectInvitationPreviewStatus);
  const previewError = useAppSelector(selectInvitationPreviewError);
  const acceptStatus = useAppSelector(selectAcceptInvitationStatus);
  const acceptError = useAppSelector(selectAcceptInvitationError);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    dispatch(fetchInvitationPreview({ token }));
  }, [dispatch, token]);

  // Stash the raw token so registration and the post-login redirect can pick it up.
  useEffect(() => {
    if (!isAuthenticated && !accepted) {
      localStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
    }
  }, [isAuthenticated, accepted, token]);

  const handleJoin = async () => {
    const result = await dispatch(acceptInvitation({ token }));
    if (acceptInvitation.fulfilled.match(result)) {
      localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      setAccepted(true);
    }
  };

  const goToLogin = (register: boolean) => {
    navigate(ROUTES.LOGIN, register ? { state: { register: true } } : undefined);
  };

  // Signing up through an invitation link accepts it during registration, so a
  // freshly signed-in invitee lands here with the invitation already Accepted —
  // that's success, not a dead link. Matching on the invitation's email keeps
  // someone else's forwarded link on the invalid path.
  const acceptedByMe =
    isAuthenticated &&
    preview?.status === OrganizationInvitationStatus.Accepted &&
    preview.email.toLowerCase() === (profile?.emailaddress ?? "").toLowerCase();

  // Once the signed-in visitor has seen a resolved preview, the stashed token has
  // done its job — clear it so future logins stop redirecting here.
  useEffect(() => {
    if (isAuthenticated && (preview !== null || previewStatus === LoadingStatus.Failed)) {
      localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
    }
  }, [isAuthenticated, preview, previewStatus]);

  if (previewStatus === LoadingStatus.Loading) {
    return (
      <p className="text-grey-30 dark:text-grey-70">
        {t("organization.invitationPage.loading", "Loading invitation…")}
      </p>
    );
  }

  if (acceptedByMe) {
    return (
      <div>
        <p className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
          <Check size={20} />
          {t("organization.invitationPage.acceptedTitle", "You're a member of {{organization}}", {
            organization: preview!.organizationName,
          })}
        </p>
        <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
          {t("organization.invitationPage.acceptedHint", "This invitation has been accepted and your account is all set.")}
        </p>
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={() => navigate(ROUTES.CALENDAR)}
        >
          {t("organization.invitationPage.goToCalendar", "Go to your calendar")}
        </button>
      </div>
    );
  }

  const invalid =
    previewStatus === LoadingStatus.Failed ||
    preview === null ||
    preview.status !== OrganizationInvitationStatus.Pending ||
    preview.isExpired;

  if (invalid && !accepted) {
    return (
      <div>
        <p className="font-medium text-grey-20 dark:text-white">
          {t("organization.invitationPage.invalidTitle", "This invitation link isn't valid anymore.")}
        </p>
        <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
          {previewError?.statusCode === 404
            ? t("organization.invitationPage.invalidNotFound", "The link may be incomplete, or the invitation was revoked.")
            : t("organization.invitationPage.invalidGeneric", "It may have expired, been revoked, or already been accepted.")}{" "}
          {t("organization.invitationPage.invalidAsk", "Ask the organization's owner to send a new invitation.")}
        </p>
      </div>
    );
  }

  if (accepted) {
    return (
      <div>
        <p className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
          <Check size={20} />
          {t("organization.invitationPage.joinedTitle", "You've joined {{organization}}", {
            organization:
              preview?.organizationName ??
              t("organization.invitationPage.joinedFallback", "the organization"),
          })}
        </p>
        <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
          {t("organization.invitationPage.joinedHint", "Please sign out and sign in again to refresh your access.")}
        </p>
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={() => dispatch(logoutAsync())}
        >
          {t("organization.invitationPage.signInAgain", "Sign in again")}
        </button>
      </div>
    );
  }

  return (
    <div>
      <p className="flex items-start gap-2 font-medium text-grey-20 dark:text-white">
        <Mail size={20} className="mt-0.5 shrink-0 text-grey-30 dark:text-grey-70" />
        <span>
          {t("organization.invitationPage.invitedAs", "You've been invited to join {{organization}} as {{role}}", {
            organization: preview!.organizationName,
            role: t(`organization.roles.${preview!.role}`, preview!.role),
          })}
        </span>
      </p>
      {!isAuthenticated ? (
        <div className="mt-4 flex flex-col gap-3 lg:flex-row">
          <button type="button" className="btn btn-primary" onClick={() => goToLogin(true)}>
            {t("organization.invitationPage.createAccount", "Create account")}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => goToLogin(false)}>
            {t("organization.invitationPage.signIn", "Sign in")}
          </button>
        </div>
      ) : (
        <div className="mt-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleJoin}
            disabled={acceptStatus === LoadingStatus.Loading}
          >
            {t("organization.invitationPage.join", "Join {{organization}}", {
              organization: preview!.organizationName,
            })}
          </button>
          {acceptError && (
            <div className="mt-4 rounded-lg bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {/* "OrganizationHasData" (409) arrives with a full server explanation;
                  other failures fall back to the thunk's message. */}
              {acceptError.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvitationAcceptCard;
